import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

export type CompanyMultiSelectOption = {
  id: string;
  name: string;
};

type CompanyMultiSelectComboProps = {
  options: readonly CompanyMultiSelectOption[];
  value: readonly string[];
  onChange: (companyIds: string[]) => void;
  disabled?: boolean;
  emptyLabel?: string;
};

function summarizeSelection(
  options: readonly CompanyMultiSelectOption[],
  selectedIds: readonly string[],
  emptyLabel: string,
): string {
  if (selectedIds.length === 0) {
    return emptyLabel;
  }

  if (options.length > 0 && selectedIds.length === options.length) {
    return `Todas as empresas (${options.length})`;
  }

  const names = options
    .filter((option) => selectedIds.includes(option.id))
    .map((option) => option.name);

  if (names.length === 1) {
    return names[0];
  }

  if (names.length <= 3) {
    return names.join(', ');
  }

  return `${names.length} empresas`;
}

export function CompanyMultiSelectCombo({
  options,
  value,
  onChange,
  disabled = false,
  emptyLabel = 'Formulário padrão',
}: CompanyMultiSelectComboProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedSet = useMemo(() => new Set(value), [value]);
  const allSelected = options.length > 0 && value.length === options.length;
  const summary = summarizeSelection(options, value, emptyLabel);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return options;
    }
    return options.filter((option) => option.name.toLowerCase().includes(query));
  }, [options, search]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  function toggleAll(checked: boolean): void {
    onChange(checked ? options.map((option) => option.id) : []);
  }

  function toggleOne(companyId: string): void {
    const next = new Set(selectedSet);
    if (next.has(companyId)) {
      next.delete(companyId);
    } else {
      next.add(companyId);
    }
    onChange(Array.from(next));
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        className="erp-input flex w-full items-center justify-between gap-2 text-left"
        onClick={() => {
          if (disabled) return;
          setOpen((current) => !current);
        }}
      >
        <span className={`truncate ${value.length === 0 ? 'text-secondary-500' : ''}`}>
          {summary}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-secondary-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-secondary-200 bg-white shadow-lg"
        >
          <div className="border-b border-secondary-100 p-2">
            <label className="relative block">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
              <input
                className="erp-input pl-8"
                value={search}
                placeholder="Buscar empresa..."
                onChange={(event) => setSearch(event.target.value)}
                autoFocus
              />
            </label>
          </div>

          <div className="max-h-56 overflow-y-auto p-1">
            <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-secondary-900 hover:bg-secondary-50">
              <input
                type="checkbox"
                checked={allSelected}
                disabled={options.length === 0}
                onChange={(event) => toggleAll(event.target.checked)}
              />
              Todas as empresas
            </label>

            <div className="my-1 border-t border-secondary-100" />

            {filtered.length === 0 ? (
              <p className="px-2 py-3 text-sm text-secondary-500">
                {options.length === 0 ? 'Nenhuma empresa cadastrada.' : 'Nenhum resultado.'}
              </p>
            ) : (
              filtered.map((option) => (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-secondary-800 hover:bg-secondary-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(option.id)}
                    onChange={() => toggleOne(option.id)}
                  />
                  <span className="truncate">{option.name}</span>
                </label>
              ))
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-secondary-100 px-2 py-1.5">
            <button
              type="button"
              className="text-xs text-secondary-600 hover:underline"
              onClick={() => onChange([])}
            >
              Limpar ({emptyLabel.toLowerCase()})
            </button>
            <button
              type="button"
              className="text-xs font-medium text-primary-700 hover:underline"
              onClick={() => {
                setOpen(false);
                setSearch('');
              }}
            >
              Fechar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
